<template>
    <div class="overview-container">
        <div class="overview-content">

            <h1 class="text-primary fade-in-up-enter-active animation-slowest text-center mb-2 mt-2">Overzicht</h1>

            <div v-if="userSituationRouteType" class="d-flex flex-column align-items-center mb-3">
                <div class="alert alert-success d-inline-flex align-items-center gap-2 py-2 px-3 mb-0 route-alert" role="alert">
                    <i class="bi bi-signpost" style="font-size:1.3em;"></i>
                    <span style="font-size:1.1em;"><strong class="fw-600">Route:</strong> {{ userSituationRouteType }}</span>
                </div>
                <div v-if="calendlyTypeName" class="alert alert-primary d-inline-flex align-items-center gap-2 py-2 px-3 mb-0 afspraak-alert" role="alert">
                    <i class="bi bi-calendar-event" style="font-size:1.2em;"></i>
                    <span style="font-size:1em;"><strong class="fw-600">Afspraak:</strong> {{ calendlyTypeName }}</span>
                </div>
            </div>

            <div
                v-if="leadSourceInfoBlocks.length"
                class="mb-3 lead-source-info-wrapper"
            >
                <div class="d-flex flex-column gap-2">
                    <div
                        v-for="(item, index) in leadSourceInfoBlocks"
                        :key="index"
                        class="rounded-3 border-start border-4 px-3 py-2 d-flex align-items-start"
                        :class="{
                            'bg-warning bg-opacity-10 border-warning text-warning-emphasis': item.type === 'warning',
                            'bg-info bg-opacity-10 border-info text-info-emphasis': item.type === 'info'
                        }"
                    >
                        <i
                            class="bi me-2 mt-1"
                            :class="{
                                'bi-exclamation-triangle-fill': item.type === 'warning',
                                'bi-info-circle-fill': item.type === 'info'
                            }"
                        />

                        <div>
                            <strong>
                                {{ item.type === 'warning' ? 'Let op:' : 'Info:' }}
                            </strong>
                            {{ item.message }}
                        </div>
                    </div>
                </div>
            </div>

            <base-card class="w-100 fade-in-up-enter-active animation-slowest delay-200" inner-class="container-wide" color="alternate" :outer-padding="true">
                
                <div class="row g-3">
                    <!-- Contact Information -->
                    <div class="col-12 col-sm-6">
                        <base-card class="h-100 full-width-card" color="light">
                            <h5 class="section-title text-success mb-2">Contactgegevens</h5>
                            <checkmark-item icon="person-fill" class="mb-1 compact">
                                <strong class="fw-600">Naam:</strong> {{ lead.contact.FirstName }} {{ lead.contact.LastName }}
                            </checkmark-item>
                            <checkmark-item icon="telephone-fill" class="mb-1 compact">
                                <strong class="fw-600">Telefoon:</strong> {{ lead.contact.Phone }}
                            </checkmark-item>
                            <checkmark-item icon="envelope-fill" class="mb-1 compact">
                                <strong class="fw-600">E-mail:</strong> {{ lead.contact.Email }}
                            </checkmark-item>
                        </base-card>
                    </div>

                    <!-- Subsidy Information -->
                    <div class="col-12 col-sm-6">
                        <base-card class="h-100 full-width-card" color="light">
                            <h5 class="section-title text-success mb-2">Subsidie</h5>
                            <checkmark-item icon="percent" class="mb-1 compact">
                                <strong class="fw-600">Percentage:</strong> {{ subsidyPercentage }}
                                <ul v-if="subsidyPercentage.startsWith('100%') || lead.financing.income.ExpectLowerIncomeThisYear === true" class="subsidy-explanation-list">
                                    <li v-if="subsidyPercentage.startsWith('100%') && lead.address.m29_100pct === true" class="subtle-bullet">Dit adres valt in een 100%-gebied.</li>
                                    <li v-if="subsidyPercentage.startsWith('100%') && !lead.address.m29_100pct && lead.financing.income.SM_140pct === true" class="subtle-bullet">De bewoner komt op basis van inkomen in aanmerking voor 100% subsidie.</li>
                                    <!-- <li v-if="lead.financing.income.ExpectLowerIncomeThisYear === true" class="subtle-bullet">De bewoner verwacht dit jaar <strong class="fw-600">toch in aanmerking te komen</strong> voor de 140%-regeling.</li> TODO: uncomment -> temporary disabled --> 
                                </ul>
                            </checkmark-item>
                            <!-- <checkmark-item icon="calendar-event" class="mb-1 compact" v-if="this.lead.address.pc_m29_open_from_string">
                                <strong class="fw-600">Openstelling:</strong> {{ this.lead.address.pc_m29_open_from_string }}
                            </checkmark-item> -->
                            <checkmark-item icon="clock-history" class="mb-1 compact">
                                <strong class="fw-600">Terugwerkende kracht hulp:</strong> {{ lead.intakeCompletion.TWK ? 'Ja' : 'Nee' }}
                            </checkmark-item>
                            <checkmark-item icon="question-circle" class="mb-1 compact">
                                <strong class="fw-600">Ondersteuning maatregel 29:</strong> {{ lead.advice.maatregel29Support === null ? 'Nog niet ingevuld' : (lead.advice.maatregel29Support ? 'Ja' : 'Nee') }}
                            </checkmark-item>
                            <a
                                v-if="subsidySummary.length"
                                href="#aanvullende-subsidies"
                                class="subsidy-summary-link compact"
                                title="Bekijk de aanvullende subsidies"
                            >
                                <i class="bi bi-arrow-down-circle me-1" aria-hidden="true"></i>
                                Bekijk aanvullende subsidies ({{ subsidySummaryText }})
                            </a>
                        </base-card>
                    </div>

                    <!-- Address Information -->
                    <div class="col-12 col-sm-6">
                        <base-card class="h-100 full-width-card" color="light">
                            <h5 class="section-title text-success mb-3">Adresgegevens</h5>
                            <checkmark-item icon="key-fill" class="mb-1 compact" v-if="lead.property.ConstructionYear !== lead.address.BAG_oorspronkelijkBouwjaar__c">
                                <strong class="fw-600">Bouwjaar volgens bewoner:</strong> {{ lead.property.ConstructionYear }}
                            </checkmark-item>
                            <checkmark-item icon="key-fill" class="mb-1 compact">
                                <strong class="fw-600">Bouwjaar BAG:</strong> {{ lead.address.BAG_oorspronkelijkBouwjaar__c }}
                            </checkmark-item>
                            <checkmark-item icon="house-fill" class="mb-2 compact">
                                <strong class="fw-600">Adres:</strong> {{ streetAddress }}
                            </checkmark-item>
                            <checkmark-item icon="mailbox" class="mb-2 compact">
                                <strong class="fw-600">Postcode:</strong> {{ postalCode }}
                            </checkmark-item>
                            <checkmark-item icon="geo-alt-fill" class="mb-2 compact">
                                <strong class="fw-600">Plaats:</strong> {{ city }}
                            </checkmark-item>
                            <checkmark-item icon="geo-alt-fill" class="mb-2 compact">
                                <strong class="fw-600">Gemeente:</strong> {{ lead.address.Municipality }}
                            </checkmark-item>
                        </base-card>
                    </div>

                    <!-- Special Conditions -->
                    <div class="col-12 col-sm-6">
                        <base-card class="h-100 full-width-card" color="light">
                            <h5 class="section-title text-success mb-2">Bijzonderheden</h5>
                            <checkmark-item icon="bank" class="mb-1 compact">
                                <strong class="fw-600">Monument:</strong> {{ lead.property.IsMonument ? 'Ja' : 'Nee' }}
                            </checkmark-item>
                            <checkmark-item icon="shield-fill-exclamation" class="mb-1 compact">
                                <strong class="fw-600">Beschermd gezicht:</strong> {{ lead.property.IsBeschermdGezicht ? 'Ja' : 'Nee' }}
                            </checkmark-item>
                            <checkmark-item icon="cone-striped" class="mb-1 compact">
                                <strong class="fw-600">Versterkingsprogramma:</strong> {{ lead.property.InVersterkingsprogramma ? 'Ja' : 'Nee' }}
                            </checkmark-item>
                        </base-card>
                    </div>

                    <!-- Property Information -->
                    <div class="col-12 col-sm-6">
                        <base-card class="h-100 full-width-card" color="light">
                            <h5 class="section-title text-success mb-2">Woninggegevens</h5>
                            <checkmark-item icon="key-fill" class="mb-1 compact">
                                <strong class="fw-600">Eigenaar:</strong> {{ lead.ownership.PropertyOwner ? 'Ja' : 'Nee' }}
                            </checkmark-item>
                            <checkmark-item icon="building" class="mb-1 compact">
                                <strong class="fw-600">VVE:</strong> {{ lead.ownership.HoaMember ? 'Ja' : 'Nee' }}
                            </checkmark-item>
                            
                            <!-- Owner details if not owner -->
                            <template v-if="!lead.ownership.PropertyOwner">
                                <hr class="my-2">
                                <div class="text-muted mb-1" style="font-size: 0.9rem; font-weight: 600;">Eigenaar:</div>
                                <checkmark-item icon="person-fill" class="mb-1 compact">
                                    <strong class="fw-600">Naam:</strong> {{ lead.ownership.OwnerName || 'Niet opgegeven' }}
                                </checkmark-item>
                                <checkmark-item icon="telephone-fill" class="mb-1 compact">
                                    <strong class="fw-600">Telefoon:</strong> {{ lead.ownership.OwnerPhone || 'Niet opgegeven' }}
                                </checkmark-item>
                                <checkmark-item icon="envelope-fill" class="mb-1 compact">
                                    <strong class="fw-600">E-mail:</strong> {{ lead.ownership.OwnerEmail || 'Niet opgegeven' }}
                                </checkmark-item>
                            </template>

                            <!-- VVE details if member -->
                            <template v-if="lead.ownership.HoaMember">
                                <hr class="my-2">
                                <div class="text-muted mb-1" style="font-size: 0.9rem; font-weight: 600;">VVE contactpersoon:</div>
                                <checkmark-item icon="person-fill" class="mb-1 compact">
                                    <strong class="fw-600">Naam:</strong> {{ lead.ownership.HoaRepName || 'Niet opgegeven' }}
                                </checkmark-item>
                                <checkmark-item icon="telephone-fill" class="mb-1 compact">
                                    <strong class="fw-600">Telefoon:</strong> {{ lead.ownership.HoaRepPhone || 'Niet opgegeven' }}
                                </checkmark-item>
                                <checkmark-item icon="envelope-fill" class="mb-1 compact">
                                    <strong class="fw-600">E-mail:</strong> {{ lead.ownership.HoaRepEmail || 'Niet opgegeven' }}
                                </checkmark-item>
                            </template>
                        </base-card>
                    </div>

                    <!-- Lead Source -->
                    <div class="col-12 col-sm-6">
                        <base-card class="h-100 full-width-card" color="light">
                            <h5 class="section-title text-success mb-2">Bron</h5>
                            <checkmark-item icon="signpost-fill" class="mb-1 compact">
                                {{ lead.meta.LeadSource || 'Niet opgegeven' }}
                            </checkmark-item>
                        </base-card>
                    </div>

                    <div id="aanvullende-subsidies" class="col-12">
                        <base-card class="h-100 full-width-card" color="light">
                            <h5 class="section-title text-success mb-2">Aanvullende subsidies</h5>
                            <div v-if="subsidyOverviewIncomplete" class="alert alert-warning d-flex align-items-start gap-2 py-2 px-3 mb-3" role="alert">
                                <i class="bi bi-exclamation-triangle-fill mt-1" />
                                <span>
                                    <strong>Let op:</strong> nog niet alle vragen voor deze subsidie(s) zijn ingevuld.
                                </span>
                            </div>
                            <div v-if="subsidySummary.length" class="subsidy-summary mb-3">
                                <strong class="fw-600">Samenvatting subsidies:</strong>
                                <span v-for="(summary, index) in subsidySummary" :key="summary.label">
                                    <span v-if="index > 0"> | </span>
                                    {{ summary.count }} {{ summary.label }}
                                </span>
                            </div>
                            <div v-if="subsidyOutcomes.length">
                                <div v-for="sub in subsidyOutcomes" :key="sub.subsidy" class="mb-2">
                                    <checkmark-item
                                        :icon="sub.isEligible ? 'check-circle-fill' : (sub.isPotential ? 'question-circle-fill' : 'x-circle-fill')"
                                        :icon-color="sub.isEligible ? 'success' : (sub.isPotential ? 'warning' : 'danger')"
                                        class="mb-1 compact subsidy-item"
                                    >
                                        <strong class="fw-600">Naam subsidie:</strong> {{ sub.subsidy }}
                                        <template v-if="sub.subsidyType">
                                            <br>
                                            <strong class="fw-600">Type subsidie:</strong> {{ sub.subsidyType }}
                                        </template>
                                        <span v-if="sub.amountText"> — {{ sub.amountText }}</span>
                                        <div v-if="getSubsidyInfo(sub.subsidy).length" class="mb-1 mt-1">
                                            <strong class="fw-600">Opmerkingen bij de aanvraag:</strong>
                                            <ul class="mb-1 mt-1 subsidy-info-list">
                                                <li v-for="info in getSubsidyInfo(sub.subsidy)" :key="info">{{ info }}</li>
                                            </ul>
                                        </div>
                                        <span
                                            v-if="sub.twk"
                                            class="twk-indicator ms-2"
                                            :class="sub.twk.isEligible ? 'twk-ok' : 'twk-not-ok'"
                                        >
                                            TWK: {{ sub.twk.isEligible ? 'mogelijk' : 'niet mogelijk' }}
                                        </span>
                                        <div class="requirement-table mt-2" v-if="sub.requirements && sub.requirements.length">
                                            <div
                                                v-for="req in sub.requirements"
                                                :key="`${sub.subsidy}-${req.rule}`"
                                                class="requirement-row"
                                            >
                                                <div class="requirement-label">{{ req.label }}</div>
                                                <div class="requirement-value">{{ req.currentValue }}</div>
                                                <div class="requirement-status" :class="req.isMet ? 'status-ok' : 'status-not-ok'">
                                                    {{ req.isMet ? 'Voldoet' : 'Niet voldaan' }}
                                                </div>
                                            </div>
                                        </div>
                                    </checkmark-item>
                                </div>
                            </div>
                            <div v-else class="text-muted">Op basis van de ingevulde gegevens komt de bewoner niet in aanmerking voor aanvullende subsidies.</div>
                        </base-card>
                    </div>
                </div>

            </base-card>
        </div>
    </div>
</template>

<script>
  import BaseCard from "@/components/BaseCard.vue";
  import CheckmarkItem from "@/components/CheckmarkItem.vue";

  import { useAllStores } from "@/helpers/UseAllStores";
  import { CommonComposable } from "@/helpers/CommonComposable";

    import { MunicipalitySubsidyService } from '@/services/MunicipalitySubsidyService';

  export default {
    components: {
      BaseCard,
      CheckmarkItem,
    },
    setup(props, context) {
      return { ...CommonComposable(props, context), ...useAllStores() };
    },
    computed: {
        streetAddress() {
            const addr = this.lead.address;
            return `${addr.Street} ${addr.StreetNumber}`;
        },
        postalCode() {
            return this.lead.address.PostalCode;
        },
        city() {
            return this.lead.address.City;
        },
        postalCodeCity() {
            return `${this.postalCode} ${this.city}`;
        },
        fullAddress() {
            return `${this.streetAddress}, ${this.postalCodeCity}`;
        },
        subsidyPercentage() {
            if (this.lead.address.m29_100pct === true || this.lead.financing.income.SM_140pct === true) {
                return '100% van de kosten tot €40.000';
            }

            return '50% van de kosten, max. €20.000';
        },
        userSituationRouteType() {
            const situation = this.lead.intakeCompletion.UserSituation;
            if (situation === 'appointment_isolatieplan' || situation === 'waiting_isolatieplan') {
                return 'Isolatieplan';
            } else if (situation === 'quotes_without_plan') {
                return '10.000 euro';
            }
            return '';
        },

        calendlyTypeName() {
            const url = this.lead.PrefilledCalendlyUrl;
            if (!url) {
                return '';
            } else if (url.includes('advies-nijbegun-30min')) {
                return 'Telefonisch adviesgesprek Nij Begun';
            } else if (url.includes('telefonisch-adviesgesprek-nij-begun-10k')) {
                return 'Telefonisch adviesgesprek: isoleren tot €10.000';
            }
            return '';
        },
        subsidyOutcomes() {
            if (!this.lead || !this.lead.address) {
                return [];
            }

            const possibleSubsidies = new Set(
                MunicipalitySubsidyService.getEligibleSubsidies(this.lead, 'financing'),
            );

            const outcomes = MunicipalitySubsidyService
                .getAllSubsidyOutcomes(this.lead, 'final')
                .map((outcome) => {
                    const requirements = MunicipalitySubsidyService.getRequirementDetails(
                        outcome.subsidy,
                        this.lead,
                        outcome.isEligible ? 'final' : (outcome.failedPhase || 'final'),
                        outcome.isEligible ? null : outcome.failedRule,
                    );
                    const hasMissingRequirement = requirements.some(
                        (requirement) => requirement.currentValue === 'onbekend',
                    );
                    const hasFailedRequirement = requirements.some(
                        (requirement) => !requirement.isMet && requirement.currentValue !== 'onbekend',
                    );

                    return {
                        outcome,
                        requirements,
                        isPotential: !hasFailedRequirement && (possibleSubsidies.has(outcome.subsidy) || hasMissingRequirement),
                    };
                })
                .filter(({ outcome, isPotential }) =>
                    outcome.isEligible || isPotential || possibleSubsidies.has(outcome.subsidy),
                )
                .map((outcome) => ({
                    subsidy: outcome.outcome.subsidy,
                    subsidyType: outcome.outcome.subsidyType,
                    isEligible: outcome.outcome.isEligible,
                    isPotential: outcome.isPotential,
                    amountText: outcome.outcome.amountText,
                    reason: outcome.outcome.reason,
                    twk: outcome.outcome.twk,
                    requirements: outcome.requirements,
                }));

            return outcomes.sort((a, b) => Number(b.isEligible) - Number(a.isEligible));
        },
        subsidyOverviewIncomplete() {
            return this.subsidyOutcomes.some((subsidy) =>
                subsidy.requirements.some((requirement) => requirement.currentValue === 'onbekend'),
            );
        },
        subsidySummary() {
            const summary = {
                possible: 0,
                notPossible: 0,
                incomplete: 0,
            };

            this.subsidyOutcomes.forEach((subsidy) => {
                const hasMissingRequirement = subsidy.requirements.some(
                    (requirement) => requirement.currentValue === 'onbekend',
                );

                if (hasMissingRequirement) {
                    summary.incomplete += 1;
                } else if (subsidy.isEligible || subsidy.isPotential) {
                    summary.possible += 1;
                } else {
                    summary.notPossible += 1;
                }
            });

            return [
                { count: summary.possible, label: 'mogelijk' },
                { count: summary.notPossible, label: 'niet mogelijk' },
                { count: summary.incomplete, label: 'nog niet ingevuld' },
            ].filter((item) => item.count > 0);
        },
        subsidySummaryText() {
            const labels = {
                mogelijk: 'mogelijk',
                'niet mogelijk': 'niet mogelijk',
                'nog niet ingevuld': 'nog in te vullen',
            };

            return this.subsidySummary
                .map((summary) => `${labels[summary.label]}: ${summary.count}`)
                .join(' | ');
        },

        leadSourceInfoBlocks() {
            const messages = [];

            if (this.lead.isVhfPekela) {
                messages.push({
                    type: 'warning',
                    message: 'VHF Pekela'
                });
            }

            if (this.lead.address.assigned_partner === 'jgh bespaaradvies') {
                messages.push({
                    type: 'warning',
                    message: 'Bewoner komt mogelijk in aanmerking voor aanvullende subsidie - Doorverwijzen naar JGH Bespaaradvies (0502340505).'
                });
            } else if (this.lead.address.assigned_partner === 'Bureau voor Verduurzamen') {
                messages.push({
                    type: 'info',
                        message: 'Bewoner komt mogelijk in aanmerking voor een aanvullende subsidie.'
                });
            }

            const bagYear = this.lead.address.BAG_oorspronkelijkBouwjaar__c;
            const constructionYear = this.lead.property.ConstructionYear;

            if (bagYear > 2012) {
                if (constructionYear && constructionYear < 2013 && constructionYear !== bagYear) {
                    messages.push({
                        type: 'info',
                        message: `Woning volgens BAG gebouwd na 2012, maar bewoner heeft ${constructionYear} aangegeven.`
                    });
                } else {
                    messages.push({
                        type: 'warning',
                        message: 'Woning volgens BAG gebouwd na 2012.'
                    });
                }
            }

            switch (this.lead.meta.LeadSource) {
                case 'meedoen.bureauverduurzamen.nl - TNEAanvraag':
                    messages.push({
                        type: 'info',
                        message: 'Deze bewoner krijgt een adviesgesprek, een offertebespreking en hulp bij de subsidieaanvraag aan de keukentafel.'
                    });
                    break;

                case 'meedoen.bureauverduurzamen.nl - Natuurlijkemomenten':
                    messages.push({
                        type: 'info',
                        message: 'Deze bewoner krijgt versneld een isolatieplan en ondersteuning van uitvoerders om te verduurzamen kort na sleuteloverdracht.'
                    });
                    break;
            }

            return messages;
        },
    },
    methods: {
        getSubsidyInfo(subsidy) {
            return MunicipalitySubsidyService.getSubsidyInfo(
                subsidy,
                this.lead.address.pc_GemNaam,
            );
        },
    },
  };
</script>

<style scoped lang="scss">
.overview-container {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    min-height: 100vh;
    padding: 1rem;
    background-color: $std-background;
}

.overview-content {
    max-width: 1000px;
    width: 100%;
}

.route-alert {
    border-bottom-left-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
    padding-bottom: 0.5em !important;
    margin-bottom: 0 !important;
}
.afspraak-alert {
    border-top-left-radius: 0 !important;
    border-top-right-radius: 0 !important;
    padding-top: 0.5em !important;
    margin-top: -2px !important;
}

.section-title {
    font-size: 1.1rem;
    font-weight: 600;
}

.compact {
    font-size: 0.95rem;
    word-break: break-word;
    overflow-wrap: break-word;
}

.full-width-card {
    width: 100%;
    max-width: 100%;
    padding: 0.8rem;
    
    ::v-deep .card-inner {
        width: 100%;
        max-width: 100%;
    }
}

.subsidy-explanation-list {
    margin: 0.2em 0 0 1.2em;
    padding: 0;
    list-style: disc;
}

.requirement-table {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
}

.twk-indicator {
    display: inline-block;
    font-size: 0.78rem;
    font-weight: 700;
    border-radius: 999px;
    padding: 0.12rem 0.5rem;
    vertical-align: middle;
}

.twk-ok {
    color: #1d6f42;
    background: rgba(25, 135, 84, 0.15);
}

.twk-not-ok {
    color: #9c2f2f;
    background: rgba(220, 53, 69, 0.15);
}

.requirement-row {
    display: grid;
    grid-template-columns: minmax(0, 1.7fr) minmax(120px, 1fr) auto;
    align-items: center;
    gap: 0.6rem;
    background: rgba(0, 0, 0, 0.03);
    border-radius: 0.4rem;
    padding: 0.35rem 0.5rem;
    font-size: 0.9rem;
}

.requirement-label {
    font-weight: 600;
}

.requirement-value {
    color: #495057;
    word-break: break-word;
}

.requirement-status {
    font-size: 0.8rem;
    font-weight: 700;
    border-radius: 999px;
    padding: 0.15rem 0.55rem;
    white-space: nowrap;
    width: 108px;
    text-align: center;
}

.subsidy-item {
    width: 100%;
}

.subsidy-summary-link {
    display: block;
    margin-top: 0.5rem;
    font-size: 0.85rem;
    color: inherit;
    text-decoration: underline;
}

.subsidy-item::v-deep(.soft-flex) {
    align-items: flex-start;
}

.subsidy-item::v-deep(.soft-flex > .ms-2) {
    flex: 1;
    min-width: 0;
}

.status-ok {
    color: #1d6f42;
    background: rgba(25, 135, 84, 0.15);
}

.status-not-ok {
    color: #9c2f2f;
    background: rgba(220, 53, 69, 0.15);
}

@media (max-width: 768px) {
    .requirement-row {
        grid-template-columns: 1fr;
        gap: 0.25rem;
    }

    .requirement-status {
        justify-self: start;
    }
}

.subtle-bullet {
    font-size: 0.93em;
    margin: 0;
    padding: 0;
}

.lead-source-info-intro {
    font-size: 0.98rem;
    font-weight: 500;
}

.lead-source-info-line {
    font-size: 0.98rem;
    font-weight: 500;
    line-height: 1.5;
}

.lead-source-info-wrapper {
    width: 100%;
    max-width: 860px;
    margin-left: auto;
    margin-right: auto;
}
</style>
